import { Chart } from "@/ui/organisms/Chart/Chart";
import { MoneyAxis } from "@/ui/organisms/Chart/Axis";
import { filterNullValuesFromChartData } from "./utils/filterChartData";
import { aggregateChartData, PeriodType } from "./utils/periodAggregation";
import { usePeriodFilter } from "./hooks/usePeriodFilter";
import { useDateRangeFilter } from "./hooks/useDateRangeFilter";
import { filterDataByDateRange, getAvailableDates } from "./utils/dateFilter";
import { getOptimalAxisConfig } from "./utils/axisUtils";

export function CashFlowProgression({
    isCondensed,
    data,
}: {
    isCondensed?: boolean;
    data?: any;
}) {
    const { selectedPeriod, periodFilterProps } = usePeriodFilter("monthly");
    const { dateRange, dateRangeFilterProps } = useDateRangeFilter();

    const availableDates = getAvailableDates(data || []);

    const dateFilteredData = filterDataByDateRange(data || [], dateRange);
    const aggregatedData = aggregateChartData(
        dateFilteredData,
        selectedPeriod.value as PeriodType,
        "cashflowProgression",
    );

    const filteredData = filterNullValuesFromChartData(aggregatedData);

    if (filteredData.length === 0) {
        return null;
    }

    const axisConfig = getOptimalAxisConfig(selectedPeriod.value as PeriodType);

    return (
        <Chart
            title="Cash Flow Progression"
            description="The company displays highly volatile cash flow with frequent and often substantial negative EBITDA and net income, indicating operational and financial instability that would increase credit risk for lenders."
            isCondensed={isCondensed}
            periodFilter={periodFilterProps}
            dateRangeFilter={{
                ...dateRangeFilterProps,
                availableDates,
            }}
            options={{
                data: filteredData,
                tooltip: {
                    mode: "single",
                },
                series: [
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "totalRevenue",
                        yName: "Total Revenue",
                        marker: {
                            enabled: false,
                        },
                    },
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "preMarketingEbitda",
                        yName: "Pre-Marketing EBITDA",
                        marker: {
                            enabled: false,
                        },
                    },
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "netIncome",
                        yName: "Net Income",
                        marker: {
                            enabled: false,
                        },
                    },
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "grossMargin",
                        yName: "Gross Margin",
                        marker: {
                            enabled: false,
                        },
                    },
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "changeInCash",
                        yName: "Change in Cash",
                        marker: {
                            enabled: false,
                        },
                    },
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "ebitda",
                        yName: "EBITDA",
                        marker: {
                            enabled: false,
                        },
                    },
                ],
                axes: [
                    axisConfig,
                    {
                        ...MoneyAxis,
                        keys: [
                            "totalRevenue",
                            "preMarketingEbitda",
                            "netIncome",
                            "grossMargin",
                            "changeInCash",
                            "ebitda",
                        ],
                        crosshair: {
                            enabled: true,
                        },
                    },
                ],
            }}
        />
    );
}
